// ---------------------------------------------------------------------------
// country.ja.ts — Japanese translations for all 5 country landing pages
// ---------------------------------------------------------------------------
import type { CountryText } from './country.content';

export const COUNTRY_JA: Record<string, CountryText> = {
  us: {
    countryName: 'アメリカ合衆国',
    heroTitle: 'アメリカ合衆国向けホテル管理ソフトウェア',
    heroSubtitle: 'アメリカのホテル、リゾート、ホスピタリティグループ向けエンタープライズPMS。米国税務コンプライアンス、PCI DSS決済処理、ADA対応デジタル体験を統合したマルチプロパティ運営管理。',
    statsLabels: ['全国のホテル数', '年間業界収益', '平均稼働率', '平均日額料金 (ADR)'],
    features: [
      { title: '米国税務エンジン', description: '全50州の一時宿泊税（TOT）、州売上税、観光改善地区料金の自動計算。' },
      { title: 'PCI DSS準拠決済', description: 'トークン化とエンドツーエンド暗号化によるPCI DSSレベル1認証決済処理。' },
      { title: 'OTAチャネルマネージャー', description: 'Booking.com、Expedia、Airbnb、VRBOなど200以上の配信チャネルとのリアルタイム同期。' },
      { title: 'ADA対応デジタル体験', description: 'ADAアクセシビリティのためのWCAG 2.1 AA準拠予約エンジンとゲストポータル。' },
      { title: 'STRベンチマーク連携', description: '競合セットに対するRevPAR、ADR、稼働率の自動ベンチマーキング。' },
      { title: 'マルチプロパティポートフォリオ', description: '統合P&L、料金管理、スタッフスケジューリングを備えたホテルグループ向け一元管理ダッシュボード。' },
    ],
    complianceTitle: '米国規制コンプライアンス内蔵',
    complianceItems: [
      { name: '州・地方税', description: '全管轄区域のTOT、売上税、TID料金の自動計算' },
      { name: 'PCI DSSレベル1', description: 'トークン化と四半期スキャン付き認証済み決済カードセキュリティ' },
      { name: 'ADAコンプライアンス', description: '予約とゲストサービスのデジタルアクセシビリティ基準' },
      { name: 'CCPA / 州プライバシー', description: 'カリフォルニア消費者プライバシー法および州レベルのデータ保護' },
      { name: 'OSHA職場安全', description: 'ホスピタリティ労働者安全追跡とインシデント報告' },
      { name: 'フォーム1099-K報告', description: 'IRS準拠のためのOTA収益自動報告' },
    ],
    testimonial: { quote: 'HotelStackはフロリダとカリフォルニアの12物件ポートフォリオを変革しました。TOT自動申告だけで月40時間を節約。マルチプロパティダッシュボードで今までにないリアルタイムの可視性を実現。', role: '運営担当副社長', hotel: 'Coastal Hospitality Group、マイアミ' },
    faqItems: [
      { question: 'HotelStackは米国の州・地方ホテル税に対応していますか？', answer: 'はい。HotelStackは全50州と主要自治体のTOT、州売上税、TID料金を自動計算します。' },
      { question: 'HotelStackはPCI DSS準拠ですか？', answer: 'HotelStackはPCI DSSレベル1認証済みです。全決済データはトークン化およびエンドツーエンド暗号化されています。' },
      { question: '1つのダッシュボードから複数の米国物件を管理できますか？', answer: 'もちろんです。マルチプロパティモジュールは統合レポート、一元料金管理、クロスプロパティゲストプロファイルを提供します。' },
      { question: 'HotelStackは米国のOTAと連携していますか？', answer: 'はい。Booking.com、Expedia、Airbnb、VRBO、Hotels.comなど200以上のチャネルに接続します。' },
    ],
    ctaTitle: '米国のホテル運営を近代化する準備はできていますか？',
    ctaSubtitle: 'HotelStackを使用して運営を最適化し、収益を最大化し、卓越したゲスト体験を提供する数百のアメリカのホテルに参加しましょう。',
  },
  uk: {
    countryName: 'イギリス',
    heroTitle: 'イギリス向けホテル管理ソフトウェア',
    heroSubtitle: '英国のホテル、ブティックプロパティ、ホスピタリティチェーン向けエンタープライズPMS。HMRC準拠のVAT自動化、Making Tax Digital対応、GDPRファーストのデータ処理。',
    statsLabels: ['ホテル・B&B数', '年間業界収益', '平均稼働率', '平均日額料金 (ADR)'],
    features: [
      { title: 'HMRC VAT自動化', description: 'Making Tax Digital（MTD）準拠のVAT申告（20%）、自動申告書作成・提出。' },
      { title: 'GDPRファーストデータ処理', description: '同意管理、削除権ワークフロー、DPOツールを備えたゲストデータ処理。' },
      { title: '英国OTAディストリビューション', description: 'Booking.com、Expedia、LateRooms、Airbnbとのリアルタイム接続で英国市場をカバー。' },
      { title: 'AA/Visit England連携', description: 'AA星評価とVisit England認定に対応した品質評価追跡。' },
      { title: '収益管理', description: '英国の季節パターンとイベントに最適化された需要予測付きダイナミックプライシングエンジン。' },
      { title: 'スタッフスケジューリング（英国労働法）', description: '英国労働時間規則と国民最低賃金に準拠したシフト管理。' },
    ],
    complianceTitle: '英国規制コンプライアンス内蔵',
    complianceItems: [
      { name: 'HMRC VAT / MTD', description: 'デジタル記録保持付きMaking Tax Digital準拠VAT申告' },
      { name: 'GDPR', description: 'ゲストデータの完全な一般データ保護規則準拠' },
      { name: '消防安全命令', description: '2005年規制改革（消防安全）命令の追跡・文書化' },
      { name: '食品衛生（FSA）', description: 'FSA評価準拠とHACCP文書化' },
      { name: '平等法2010', description: '障害のあるゲストとスタッフのアクセシビリティ要件' },
      { name: '就労権確認', description: '従業員確認のための入管法準拠' },
    ],
    testimonial: { quote: 'コッツウォルズのブティックホテルグループのためにHotelStackに切り替えました。MTD準拠のVATモジュールとGDPRツールはまさに必要としていたものでした。開始以来、ゲスト満足度スコアが18%向上。', role: 'マネージングディレクター', hotel: 'Cotswold Heritage Hotels、オックスフォードシャー' },
    faqItems: [
      { question: 'HotelStackはMaking Tax Digital（MTD）準拠ですか？', answer: 'はい。HotelStackはデジタル記録保持付きMTD準拠VAT申告書を作成します。HMRCに直接提出できます。' },
      { question: 'HotelStackは英国ホテルのGDPRをどのように処理しますか？', answer: 'HotelStackは同意管理、自動データ保持ポリシー、削除権ワークフロー、DPOダッシュボードツールを含みます。' },
      { question: 'HotelStackは英国固有のOTAチャネルに対応していますか？', answer: 'はい。Booking.com、Expedia、Airbnb、LateRooms、Trivagoなど英国の主要チャネルすべてと連携しています。' },
      { question: 'HotelStackは英国観光の季節料金を管理できますか？', answer: '収益管理エンジンは英国の季節需要パターン、祝日料金、イベントベースのサーチャージに対応しています。' },
    ],
    ctaTitle: '英国のホテル運営を変革する準備はできていますか？',
    ctaSubtitle: 'HotelStackを使用してコンプライアンスを自動化し、直接予約を増やし、卓越したゲスト体験を提供する英国の一流ホテルに参加しましょう。',
  },
  uae: {
    countryName: 'アラブ首長国連邦',
    heroTitle: 'UAE向けホテル管理ソフトウェア',
    heroSubtitle: 'UAEのラグジュアリーホテル、リゾート、ホスピタリティグループ向けエンタープライズPMS。DTCM準拠運営、FTA VAT自動化、多言語ゲスト体験。',
    statsLabels: ['7首長国のホテル数', '年間観光収入', '平均稼働率', '平均日額料金 (ADR)'],
    features: [
      { title: 'FTA VAT自動化', description: '宿泊・F&BのVAT 5%自動計算、申告、FTA税務申告書提出。' },
      { title: 'DTCMコンプライアンス', description: '観光ディルハム徴収、分類報告、DTCMライセンス管理。' },
      { title: 'アラビア語＋英語インターフェース', description: 'アラビア語のゲストコミュニケーションと請求書付き完全バイリンガルRTL/LTRインターフェース。' },
      { title: 'eチャネル連携', description: '入国管理コンプライアンスのための警察eチャネルシステムへの自動ゲストデータ送信。' },
      { title: 'WPS給与連携', description: 'UAE賃金保護システム要件に対応したスタッフ給与処理。' },
      { title: 'ラグジュアリー収益最適化', description: 'グループ・MICEレート管理付きプレミアムセグメント向けAIダイナミックプライシング。' },
    ],
    complianceTitle: 'UAE規制コンプライアンス自動化',
    complianceItems: [
      { name: 'FTA VAT申告', description: 'VAT 5%自動計算とFTA申告書提出' },
      { name: '観光ディルハム', description: '1泊あたりの観光料金徴収とDTCM送金' },
      { name: '市政料金', description: '客室収入に対する首長国別市政料金' },
      { name: 'eチャネルゲスト報告', description: '警察システムへの自動ゲストデータ送信' },
      { name: 'DTCMホテルライセンス', description: '分類と営業ライセンスのコンプライアンス追跡' },
      { name: 'PDPLデータ保護', description: 'ゲスト記録のUAE個人データ保護法準拠' },
    ],
    testimonial: { quote: 'HotelStackは5つ星リゾートの運営をシームレスに管理しています。DTCM連携と観光ディルハム自動化により手動報告が完全になくなりました。ドバイのチームは毎日300以上のチェックインを処理。', role: 'ゼネラルマネージャー', hotel: 'Palm Residences Resort & Spa、ドバイ' },
    faqItems: [
      { question: 'HotelStackはUAEのVATと観光ディルハム料金に対応していますか？', answer: 'はい。HotelStackはVAT 5%を自動計算し、観光ディルハム料金（1部屋1泊7-20 AED）を徴収します。' },
      { question: 'HotelStackはUAE警察eチャネルにゲストデータを送信できますか？', answer: 'HotelStackは警察eチャネルシステムと連携し、自動ゲスト登録を行います。' },
      { question: 'HotelStackはアラビア語に対応していますか？', answer: 'はい。スタッフとゲスト向けのアラビア語RTLサポート付き完全バイリンガルインターフェースを提供しています。' },
      { question: 'HotelStackはUAEのラグジュアリーホテルに適していますか？', answer: 'もちろんです。MICE管理やスパ連携などの高度な機能を備え、5つ星ホテルとリゾートにサービスを提供しています。' },
    ],
    ctaTitle: 'UAEのホテル運営を向上させましょう',
    ctaSubtitle: 'HotelStackを使用してコンプライアンスを自動化し、収益を最適化し、世界クラスのゲスト体験を提供するUAEのプレミアムホテルに参加しましょう。',
  },
  thailand: {
    countryName: 'タイ',
    heroTitle: 'タイ向けホテル管理ソフトウェア',
    heroSubtitle: 'タイのホテル、リゾート、ホスピタリティ事業向けエンタープライズPMS。TM.30入国管理コンプライアンス、タイ歳入局連携、多言語サポート。',
    statsLabels: ['ホテル・リゾート数', '観光GDP貢献', '平均稼働率', '平均日額料金 (ADR)'],
    features: [
      { title: 'TM.30自動提出', description: 'チェックインから24時間以内の入国管理局への外国人ゲストTM.30自動通知。' },
      { title: 'タイVATエンジン', description: 'VAT 7%計算、請負業者支払いの源泉徴収税、歳入局申告。' },
      { title: 'タイ語＋英語＋中国語', description: 'スタッフとゲスト向けタイ語、英語、簡体字中国語の3言語インターフェース。' },
      { title: 'ホテル法（Ror Ror 1）', description: 'タイホテル法要件のライセンス追跡とコンプライアンス文書化。' },
      { title: '観光庁連携', description: 'TAT登録、SHA Plus認証追跡、観光統計報告。' },
      { title: '季節別収益管理', description: 'タイのハイ/ローシーズンに最適化されたダイナミックプライシング、フェスティバル・イベントサーチャージ対応。' },
    ],
    complianceTitle: 'タイ規制コンプライアンス自動化',
    complianceItems: [
      { name: 'TM.30入国管理', description: '到着から24時間以内の外国人自動通知' },
      { name: '歳入局VAT', description: 'VAT 7%申告と源泉徴収税コンプライアンス' },
      { name: 'ホテル法ライセンス', description: 'Ror Ror 1ホテル営業ライセンスコンプライアンス追跡' },
      { name: 'PDPAデータ保護', description: 'ゲストデータのタイPDPA準拠' },
      { name: '物品税ライセンス', description: '酒類・娯楽ライセンス管理（Por Sor 1）' },
      { name: 'FDA食品ライセンス', description: 'ホテルレストラン用Sor Bor 1食品施設ライセンス' },
    ],
    testimonial: { quote: 'TM.30自動化だけでHotelStackへの切り替えが正当化されました。プーケットで毎週200以上の外国人チェックインを処理し、入国管理コンプライアンスは完全自動化。ダイナミックプライシングで収益22%増。', role: '運営ディレクター', hotel: 'Andaman Bay Resorts、プーケット' },
    faqItems: [
      { question: 'HotelStackはTM.30外国人通知を自動化していますか？', answer: 'はい。HotelStackは外国人ゲストのチェックインから24時間以内にTM.30通知を自動作成・提出します。' },
      { question: 'HotelStackはタイのVATと源泉徴収税に対応していますか？', answer: 'HotelStackは宿泊・F&BのVAT 7%を計算し、源泉徴収税を管理し、税務申告書を作成します。' },
      { question: 'HotelStackはタイ語に対応していますか？', answer: 'はい。運営、コミュニケーション、請求書、レポートのタイ語・英語・中国語インターフェースに対応しています。' },
      { question: 'HotelStackはタイのリゾート運営を管理できますか？', answer: 'もちろんです。スパ管理、プール/ビーチサービス、エクスカーション予約、マルチアウトレットF&B運営に対応。' },
    ],
    ctaTitle: 'タイのホテル運営を近代化しましょう',
    ctaSubtitle: 'HotelStackを使用してコンプライアンスを自動化し、収益を向上させ、記憶に残るゲスト体験を創出するタイの一流ホテル・リゾートに参加しましょう。',
  },
  australia: {
    countryName: 'オーストラリア',
    heroTitle: 'オーストラリア向けホテル管理ソフトウェア',
    heroSubtitle: 'オーストラリアのホテル、リゾート、宿泊施設向けエンタープライズPMS。ATO準拠GST自動化、BAS申告、スーパーアニュエーション管理。',
    statsLabels: ['ホテル・宿泊施設数', '年間観光収入', '平均稼働率', '平均日額料金 (ADR)'],
    features: [
      { title: 'ATO GST & BAS申告', description: 'GST 10%自動計算、四半期Business Activity Statement作成、ATO提出。' },
      { title: 'スーパーアニュエーション管理', description: 'SuperStream準拠の支払いによる11.5%のスーパー保証拠出金自動化。' },
      { title: 'プライバシー法準拠', description: 'ゲストデータ収集・保存・処理に組み込まれたオーストラリアプライバシー原則（APPs）。' },
      { title: 'AU/NZ OTAディストリビューション', description: 'Booking.com、Expedia、Airbnb、Stayz、Wotifとのオーストラリア市場向け接続。' },
      { title: '遠隔地プロパティサポート', description: '地方・遠隔地の宿泊施設向け低帯域幅モードとオフライン機能。' },
      { title: 'Tourism Australia連携', description: '星評価対応、品質認定追跡、ABS観光統計報告。' },
    ],
    complianceTitle: 'オーストラリア規制コンプライアンス内蔵',
    complianceItems: [
      { name: 'GST / BAS申告', description: 'GST 10%自動計算と四半期Business Activity Statements' },
      { name: 'PAYG源泉徴収', description: '従業員税源泉徴収とATO自動送金' },
      { name: 'スーパー保証', description: 'SuperStream準拠の11.5%スーパー拠出金' },
      { name: 'プライバシー法（APPs）', description: 'ゲストデータ保護のためのオーストラリアプライバシー原則' },
      { name: 'WHS準拠', description: 'ホスピタリティスタッフの労働安全衛生法追跡' },
      { name: '酒類免許', description: '州別酒類免許追跡と更新通知' },
    ],
    testimonial: { quote: 'HotelStackはBAS申告とスーパー拠出金を完璧に処理しています。シドニーからケアンズまで8物件を運営していますが、マルチプロパティダッシュボードで即座に可視性を得られます。スタッフは直感的なインターフェースを気に入っています。', role: '最高執行責任者', hotel: 'Pacific Coast Hotels、シドニー' },
    faqItems: [
      { question: 'HotelStackはオーストラリアのGSTとBAS申告に対応していますか？', answer: 'はい。HotelStackはGST 10%を自動計算し、四半期BASを作成し、ATO直接提出に対応しています。' },
      { question: 'HotelStackはホテルスタッフのスーパーアニュエーションを管理できますか？', answer: 'HotelStackは11.5%のスーパー保証拠出金を計算・追跡し、SuperStream準拠の支払いファイルを作成します。' },
      { question: 'HotelStackはオーストラリアのOTAと連携していますか？', answer: 'はい。Booking.com、Expedia、Airbnb、Stayz、Wotifなど200以上のチャネルに接続します。' },
      { question: 'HotelStackはオーストラリアの地方プロパティに適していますか？', answer: 'もちろんです。低帯域幅モードとオフライン機能を備え、接続回復時にデータを同期します。' },
    ],
    ctaTitle: 'オーストラリアのホテル運営を変革しましょう',
    ctaSubtitle: 'HotelStackを使用してコンプライアンスを自動化し、収益を最適化し、卓越したゲスト体験を提供するオーストラリアの一流ホテルに参加しましょう。',
  },
};
